// ─── operatorActions.js ──────────────────────────────────────────────────────
// The Operator's action registry — the ONLY actions the ASK ME assistant can
// take, 6 Oct 2026.
//
// Safety model, in order:
//   1. The LLM (operator edge function) only ever PROPOSES an action, chosen
//      from this catalog, with plain-language summary. It executes nothing.
//   2. The user confirms the proposal in the drawer. No confirm, no action.
//   3. Execution happens HERE, in the browser, through the same /api/data
//      route the dashboard buttons use — as the logged-in user, with their
//      role enforced server-side. The model never holds write credentials.
//
// Every action resolves its target itself and REFUSES on ambiguity (listing
// the candidates) rather than guessing. Add new actions here and they become
// proposable automatically — the catalog is sent to the LLM on every ask.

import { masterSupabase, getUserRole } from "../lib/supabase.js";

async function api(path, options = {}) {
  const { data: { session } } = await masterSupabase.auth.getSession();
  const res = await fetch(path, {
    ...options,
    headers: { Authorization: `Bearer ${session?.access_token}`, "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json.data;
}
const dataOp = (op) => api("/api/data", { method: "POST", body: JSON.stringify(op) });

const EDIT_ROLES = ["owner", "admin", "editor"];

// ─── Resolvers ───────────────────────────────────────────────────────────────

async function resolveAgent(nameOrSlug) {
  const q = String(nameOrSlug || "").toLowerCase().trim();
  const rows = await dataOp({ op: "select", table: "agents", filters: {}, limit: 50 });
  const hits = (rows || []).filter((a) => a.slug.includes(q.replace(/\s+/g, "-")) || a.name.toLowerCase().includes(q));
  if (hits.length === 1) return hits[0];
  if (hits.length === 0) throw new Error(`No agent matches "${nameOrSlug}". Agents: ${(rows || []).map((a) => a.name).join(", ")}.`);
  throw new Error(`"${nameOrSlug}" matches ${hits.length} agents: ${hits.map((a) => a.name).join(", ")}. Say which one.`);
}

async function resolveLead(identifier) {
  const q = String(identifier || "").toLowerCase().trim();
  if (!q) throw new Error("Which lead? Give a name or email.");
  const rows = await dataOp({ op: "select", table: "chat_leads", filters: {}, order: { column: "created_at", ascending: false }, limit: 300 });
  const hits = (rows || []).filter((l) => (l.name || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q));
  if (hits.length === 1) return hits[0];
  if (hits.length === 0) throw new Error(`No lead matches "${identifier}".`);
  throw new Error(`"${identifier}" matches ${hits.length} leads: ${hits.slice(0, 5).map((l) => `${l.name} (${l.email})`).join("; ")}${hits.length > 5 ? "…" : ""}. Be more specific — the email works best.`);
}

// ─── The catalog ─────────────────────────────────────────────────────────────
// description + params are what the LLM sees. run() is what executes.

export const OPERATOR_ACTIONS = [
  {
    name: "run_agent",
    description: "Run one of the autonomous agents right now (Location Steward, Insight Agent, Content Agent, Compliance Agent, Retention Agent, Lead Shepherd). Retention runs as a safe dry-run preview.",
    params: { agent: "agent name or slug" },
    roles: EDIT_ROLES,
    summary: (a) => `Run the ${a.agent} agent now`,
    run: async (a) => {
      const agent = await resolveAgent(a.agent);
      const result = await api("/api/agents", { method: "POST", body: JSON.stringify({ slug: agent.slug }) });
      const bits = Object.entries(result || {}).filter(([k, v]) => typeof v === "number" || typeof v === "string").slice(0, 6).map(([k, v]) => `${k}: ${v}`);
      return `Ran ${agent.name}. ${bits.join(" · ") || "Completed."}`;
    },
  },
  {
    name: "pause_agent",
    description: "Pause (disable) an agent so its scheduled runs skip until resumed.",
    params: { agent: "agent name or slug" },
    roles: EDIT_ROLES,
    summary: (a) => `Pause the ${a.agent} agent`,
    run: async (a) => {
      const agent = await resolveAgent(a.agent);
      await dataOp({ op: "update", table: "agents", filters: { slug: agent.slug }, values: { enabled: false, updated_at: new Date().toISOString() } });
      return `${agent.name} is paused. Its scheduled runs will skip until you resume it.`;
    },
  },
  {
    name: "resume_agent",
    description: "Resume (enable) a paused agent.",
    params: { agent: "agent name or slug" },
    roles: EDIT_ROLES,
    summary: (a) => `Resume the ${a.agent} agent`,
    run: async (a) => {
      const agent = await resolveAgent(a.agent);
      await dataOp({ op: "update", table: "agents", filters: { slug: agent.slug }, values: { enabled: true, updated_at: new Date().toISOString() } });
      return `${agent.name} is back on its schedule.`;
    },
  },
  {
    name: "set_lead_status",
    description: "Change a lead's status. Valid statuses: new, contacted, qualified, closed.",
    params: { lead: "lead name or email", status: "new | contacted | qualified | closed" },
    roles: EDIT_ROLES,
    summary: (a) => `Mark the lead "${a.lead}" as ${a.status}`,
    run: async (a) => {
      const status = String(a.status || "").toLowerCase();
      if (!["new", "contacted", "qualified", "closed"].includes(status)) throw new Error(`"${a.status}" isn't a valid status (new, contacted, qualified, closed).`);
      const lead = await resolveLead(a.lead);
      const values = { status, updated_at: new Date().toISOString() };
      if (status === "contacted" && !lead.contacted_at) values.contacted_at = new Date().toISOString();
      await dataOp({ op: "update", table: "chat_leads", filters: { id: lead.id }, values });
      return `${lead.name} (${lead.email}) is now ${status}.${status !== "new" ? " Any unworked-lead finding for them clears on the Lead Shepherd's next pass." : ""}`;
    },
  },
  {
    name: "assign_lead",
    description: "Assign a lead to a team member by their email.",
    params: { lead: "lead name or email", assignee: "team member's email" },
    roles: EDIT_ROLES,
    summary: (a) => `Assign the lead "${a.lead}" to ${a.assignee}`,
    run: async (a) => {
      const lead = await resolveLead(a.lead);
      const email = String(a.assignee || "").trim().toLowerCase();
      if (!email.includes("@")) throw new Error("Assignee must be an email address.");
      await dataOp({ op: "update", table: "chat_leads", filters: { id: lead.id }, values: { assigned_to: email, updated_at: new Date().toISOString() } });
      return `${lead.name} is assigned to ${email}.`;
    },
  },
  {
    name: "dismiss_finding",
    description: "Dismiss an open agent finding (from the Findings tab) whose title matches the given text. Refuses if the text matches more than one open finding.",
    params: { matching: "a distinctive phrase from the finding's title" },
    roles: EDIT_ROLES,
    summary: (a) => `Dismiss the open finding matching "${a.matching}"`,
    run: async (a) => {
      const q = String(a.matching || "").toLowerCase().trim();
      if (q.length < 4) throw new Error("Give a longer phrase from the finding's title.");
      const [agentF, locF] = await Promise.all([
        dataOp({ op: "select", table: "agent_findings", filters: { status: "open" }, limit: 300 }),
        dataOp({ op: "select", table: "location_findings", filters: { status: "open" }, limit: 500 }),
      ]);
      const hits = [
        ...(agentF || []).filter((f) => (f.title || "").toLowerCase().includes(q)).map((f) => ({ ...f, _t: "agent_findings", _label: f.title })),
        ...(locF || []).filter((f) => ((f.summary || f.title || f.finding_type || "")).toLowerCase().includes(q)).map((f) => ({ ...f, _t: "location_findings", _label: f.summary || f.finding_type })),
      ];
      if (hits.length === 0) throw new Error(`No open finding matches "${a.matching}".`);
      if (hits.length > 1) throw new Error(`"${a.matching}" matches ${hits.length} open findings: ${hits.slice(0, 4).map((h) => `"${h._label}"`).join("; ")}${hits.length > 4 ? "…" : ""}. Use a more distinctive phrase.`);
      const f = hits[0];
      await dataOp({ op: "update", table: f._t, filters: { id: f.id }, values: { status: "dismissed", dismissed_at: new Date().toISOString(), dismissed_by: "operator (confirmed in Ask me)" } });
      return `Dismissed: "${f._label}".`;
    },
  },
];

// Catalog as the LLM sees it, filtered to what THIS user's role may do.
export function actionCatalogForRole() {
  const role = getUserRole();
  return OPERATOR_ACTIONS
    .filter((a) => a.roles.includes(role))
    .map(({ name, description, params }) => ({ name, description, params }));
}

export async function runOperatorAction(tool, args) {
  const action = OPERATOR_ACTIONS.find((a) => a.name === tool);
  if (!action) throw new Error(`Unknown action "${tool}".`);
  if (!action.roles.includes(getUserRole())) throw new Error("Your role can't take that action.");
  return action.run(args || {});
}

export function summarizeAction(tool, args) {
  const action = OPERATOR_ACTIONS.find((a) => a.name === tool);
  return action ? action.summary(args || {}) : tool;
}
